import { useCallback } from 'react';
import { isGpcRouterResponse } from './gpcHandoff';
import { extractMrRouterResponse } from './mrHandoff';
import { extractConstructionSowRouterResponse } from './constructionSowHandoff';
import { extractServicesPwsRouterResponse } from './servicesPwsHandoff';
import { listSessions, postV2Turn, postLazyCreateV2Turn, HomeChatHttpError, extractHomeChatCardResponse, extractDirectPolicyResponse } from './api';
import type { ChatMessage } from './types';
import type { Turn, ClarificationEnvelope, TopicClarificationEnvelope, FarRegime } from '../FarChat/types';
import { FastAnswerRecoveryError, isClarificationEnvelope, isTopicClarificationEnvelope } from '../FarChat/types';
import { resetInFlightTurnState } from '../chatTurnState';
import { HomeChatPanelProps as Props } from './HomeChatPanel.types';
import { REFUSAL_COPY, friendlyTurnErrorMessage, isTurnInProgressError, isExpectedCancellationError, isUnavailableWorkflowError } from './HomeChatTurnErrors';
import { INTENT_CHOICE_UNAVAILABLE_COPY } from './homeChatChoiceUtils';
import { PENDING_ID, makeRequestId } from './homeChatSessionUtils';
import type { HomeChatHydration } from './useHomeChatHydration';
import type { HomeChatPanelState } from './useHomeChatPanelState';
import type { HomeChatSessionReconciliation } from './useHomeChatSessionReconciliation';


type Inputs = Pick<HomeChatPanelState, 'setNewPurchaseGuidanceFocusId'> &
Pick<Props, 'pageContext'> &
Pick<HomeChatHydration,
  | 'isStaleResolve'
  | 'applyConstructionSowChatView'
  | 'applyServicesPwsChatView'
  | 'applyMrChatView'
  | 'hydrateDirectSearchThread'
  | 'applyGpcChatView'
  | 'replaceChatCard'
  | 'notify'
  | 'clearPendingIfOwned'
> &
Pick<HomeChatPanelState,
  | 'deliverResponseLlmResultEvents'
  | 'scrollThreadToLatest'
  | 'setPendingGpcRouter'
  | 'setPendingClarification'
  | 'maybePlayClarificationSound'
  | 'setPendingTopicClarification'
  | 'setTurns'
  | 'interactionRevisionRef'
  | 'setInFlightUserMsg'
  | 'setInFlightClarification'
  | 'setInFlightTopicFocus'
  | 'setPendingRequestIdSynced'
  | 'setInFlight'
  | 'activeSessionIdRef'
  | 'setActiveSessionId'
  | 'setSessions'
  | 'setMessages'
> &
Pick<HomeChatSessionReconciliation,
  | 'scheduleInFlightTitleRefresh'
  | 'refreshAfterTurnDispatch'
  | 'refreshSessions'
  | 'reconcileExpectedCancellation'
>;

export function useHomeChatTurnSubmission({
  pageContext,
  isStaleResolve,
  deliverResponseLlmResultEvents,
  applyConstructionSowChatView,
  applyServicesPwsChatView,
  applyMrChatView,
  hydrateDirectSearchThread,
  scrollThreadToLatest,
  applyGpcChatView,
  setPendingGpcRouter,
  replaceChatCard,
  setPendingClarification,
  maybePlayClarificationSound,
  setPendingTopicClarification,
  setTurns,
  interactionRevisionRef,
  setInFlightUserMsg,
  setInFlightClarification,
  setInFlightTopicFocus,
  setPendingRequestIdSynced,
  setInFlight,
  activeSessionIdRef,
  setActiveSessionId,
  scheduleInFlightTitleRefresh,
  refreshAfterTurnDispatch,
  setSessions,
  refreshSessions,
  reconcileExpectedCancellation,
  setMessages,
  notify,
  clearPendingIfOwned,
  setNewPurchaseGuidanceFocusId,
}: Inputs) {

  /**
   * Fire a turn against an *explicit* target session id, bypassing the
   * `activeSessionId` closure read inside `handleSend`. Used by the
   * priming effect when both `initialSessionId` and `initialMessage` are
   * supplied: we cannot rely on `handleSend` because its `useCallback`
   * closure still sees the pre-priming `activeSessionId` immediately
   * after `setActiveSessionId(initialSessionId)` is dispatched.
   */
  /**
   * Send a v2 turn against a concrete session id. The caller resolves any
   * ``'new'`` placeholder before calling; Home Chat never retries a v2
   * failure through a legacy v1 route.
   */
  const acceptCompanionResponse = useCallback(async (raw: unknown, sessionId: string, crid?: string): Promise<void> => {
      const envelope = raw as Record<string, unknown>;
      const resp = (envelope && typeof envelope === 'object' && envelope.turn
        ? { ...(envelope.turn as object), ...(envelope.chat_card ? { chat_card: envelope.chat_card } : {}), ...(envelope.llm_result_events ? { llm_result_events: envelope.llm_result_events } : {}) }
        : raw) as Awaited<ReturnType<typeof postV2Turn>>;
          // Stale-resolve guard: if the user navigated away (switch /
          // new-chat / delete cleared `pendingRequestId`) while this
          // fetch was in flight, do NOT mutate the now-foreign pane.
      if (isStaleResolve(crid)) return;
      deliverResponseLlmResultEvents(resp);
      const constructionSowResponse = extractConstructionSowRouterResponse(resp);
      if (constructionSowResponse) {
        applyConstructionSowChatView(constructionSowResponse);
        return;
      }
      const servicesPwsResponse = extractServicesPwsRouterResponse(resp);
      if (servicesPwsResponse) {
        applyServicesPwsChatView(servicesPwsResponse);
        return;
      }
      const mrResponse = extractMrRouterResponse(resp);
      if (mrResponse) {
        // Market Research is a separate owner-bound controller surface. Its
        // typed envelope must never fall through to the FAR/RFO Turn cast,
        // which would otherwise render an empty answer shell.
        applyMrChatView(mrResponse);
        return;
      }
      const directPolicyResponse = extractDirectPolicyResponse(resp);
      if (directPolicyResponse) {
        if (await hydrateDirectSearchThread(
          directPolicyResponse.session_id,
          () => !isStaleResolve(crid),
        )) scrollThreadToLatest();
        return;
      }
      if (isGpcRouterResponse(resp)) {
        // Exhaustive router branch: every GPC response exits before it
        // can reach Q&A clarification guards or `Turn[]` rendering.
        switch (resp.kind) {
          case 'chat':
            applyGpcChatView(resp.view);
            return;
          case 'start_confirmation':
          case 'ambiguous_choice':
          case 'resume_selection':
            setPendingGpcRouter(resp);
            return;
          case 'revision':
            setPendingGpcRouter(null);
            return;
        }
      }
      const cardResponse = extractHomeChatCardResponse(resp);
      if (cardResponse && !('turn_id' in resp)) {
            if (cardResponse.chat_card.kind === 'purchase_guidance') {
              setNewPurchaseGuidanceFocusId(cardResponse.chat_card.card_id);
            }
            replaceChatCard(cardResponse.chat_card);
            scrollThreadToLatest();
            const cardSessionId = cardResponse.session_id ?? sessionId;
            await hydrateDirectSearchThread(
              cardSessionId,
              () => !isStaleResolve(crid),
            );
        return;
      }
      const nonCardResponse = resp as Turn | ClarificationEnvelope | TopicClarificationEnvelope;
      if (isClarificationEnvelope(nonCardResponse)) {
            // Vague query — render the transient ClarificationCard. The
            // resolving resubmit targets THIS same session id.
            setPendingClarification({ ...nonCardResponse, session_id: sessionId });
            maybePlayClarificationSound(nonCardResponse, sessionId, crid);
            scrollThreadToLatest();
        return;
      }
      if (isTopicClarificationEnvelope(nonCardResponse)) {
            // Broad readers — render the transient TopicFocusCard. The
            // resolving resubmit targets THIS same session id.
            setPendingTopicClarification({ ...nonCardResponse, session_id: sessionId });
            maybePlayClarificationSound(nonCardResponse, sessionId, crid);
            scrollThreadToLatest();
        return;
      }
      setPendingClarification(null);
      setPendingTopicClarification(null);
          // All non-turn Home Chat envelopes exit above. TypeScript cannot
          // express that runtime discriminant across the legacy response
          // union, so narrow once at the rendering boundary.
          const turn = nonCardResponse as Turn;
          setTurns((prev) => [...prev, turn]);
          if (cardResponse?.chat_card.kind === 'training_action') {
            replaceChatCard(cardResponse.chat_card);
          }
          scrollThreadToLatest('answer-start');
          // The user bubble renders from `turn.user_msg`; the in-flight
          // optimistic bubble (driven by `inFlightUserMsg`) is cleared in
          // `handleSend`'s finally block.
      return;
    }, [isStaleResolve, applyConstructionSowChatView, applyServicesPwsChatView, applyGpcChatView, applyMrChatView, replaceChatCard, hydrateDirectSearchThread, maybePlayClarificationSound, deliverResponseLlmResultEvents, scrollThreadToLatest, setPendingClarification, setPendingGpcRouter, setPendingTopicClarification, setTurns]);

  const sendV2Turn = useCallback(async (
    sessionId: string, trimmed: string, crid?: string, finalQuestion?: string, regime?: FarRegime,
    focus?: { focusToken?: string; topicFocus?: string; priorClarificationQuestions?: string[] },
  ): Promise<void> => {
    const response = await postV2Turn(sessionId, trimmed, crid, finalQuestion, regime, focus, pageContext ? { pageContext } : undefined);
    await acceptCompanionResponse(response, sessionId, crid);
  }, [acceptCompanionResponse, pageContext]);

  /**
   * Lazy-create variant of ``sendV2Turn``: posts to ``/sessions/new/v2/turn``
   * which mints a session AND dispatches a v2 turn in one round-trip.
   * Returns the freshly-allocated session id (or null for a stale resolve).
   */
  const sendLazyCreateV2Turn = useCallback(
    async (
      trimmed: string,
      crid?: string,
      finalQuestion?: string,
      regime?: FarRegime,
      focus?: {
        focusToken?: string;
        topicFocus?: string;
        priorClarificationQuestions?: string[];
      },
    ): Promise<string | null> => {
      const resp = await postLazyCreateV2Turn(
        trimmed,
        crid,
        finalQuestion,
        regime,
        focus,
        pageContext ? { pageContext } : undefined,
      );
          // Stale-resolve guard: the user navigated away while this
          // lazy-create was in flight → `pendingRequestId` no longer
          // equals our captured `crid`. Do NOT touch the foreign pane.
          // Return null so the caller also skips `setActiveSessionId`.
      if (isStaleResolve(crid)) return null;
      deliverResponseLlmResultEvents(resp);
          // Route the clarification-envelope discriminator through the
          // exported `isClarificationEnvelope` type guard (L4 consistency)
          // rather than hand-rolling the `.status` check. The lazy-create
          // response is `{ session_id, turn } | ClarificationEnvelope`; the
          // guard only narrows `Turn | ClarificationEnvelope`, so pass it
          // the union the guard understands.
      const constructionSowResponse = extractConstructionSowRouterResponse(resp);
      if (constructionSowResponse) {
        applyConstructionSowChatView(constructionSowResponse);
        return constructionSowResponse.home_chat_session_id || null;
      }
      const servicesPwsResponse = extractServicesPwsRouterResponse(resp);
      if (servicesPwsResponse) {
        applyServicesPwsChatView(servicesPwsResponse);
        return servicesPwsResponse.home_chat_session_id || null;
      }
      const mrResponse = extractMrRouterResponse(resp);
      if (mrResponse) {
        // The direct first-message path can create/bind MR in one response.
        // Adopt its Home session and render the MR controller, never Q&A.
        applyMrChatView(mrResponse);
        return mrResponse.home_chat_session_id || null;
      }
      const directPolicyResponse = extractDirectPolicyResponse(resp);
      if (directPolicyResponse) {
        if (await hydrateDirectSearchThread(
          directPolicyResponse.session_id,
          () => !isStaleResolve(crid),
        )) scrollThreadToLatest();
        return directPolicyResponse.session_id;
      }
      if (isGpcRouterResponse(resp)) {
        // See the concrete-session path above: no router envelope may
        // fall through into the lazy-create `{session_id, turn}` cast.
        switch (resp.kind) {
          case 'chat':
            applyGpcChatView(resp.view);
            return null;
          case 'start_confirmation':
          case 'ambiguous_choice':
          case 'resume_selection':
            setPendingGpcRouter(resp);
            return 'homeChatSessionId' in resp
              ? resp.homeChatSessionId || null
              : null;
          case 'revision':
            setPendingGpcRouter(null);
            return resp.homeChatSessionId || null;
        }
      }
      const cardResponse = extractHomeChatCardResponse(resp);
      if (cardResponse && !('turn' in resp)) {
            if (cardResponse.chat_card.kind === 'purchase_guidance') {
              setNewPurchaseGuidanceFocusId(cardResponse.chat_card.card_id);
            }
            replaceChatCard(cardResponse.chat_card);
            scrollThreadToLatest();
            const sessionId = cardResponse.session_id || null;
            if (sessionId) {
              await hydrateDirectSearchThread(
                sessionId,
                () => !isStaleResolve(crid),
              );
            }
        return sessionId;
      }
      if (
            isClarificationEnvelope(
              resp as Turn | ClarificationEnvelope | TopicClarificationEnvelope
            )
      ) {
            // Vague query on the lazy-create path. The backend already
            // minted a real session and returned its id on the envelope;
            // adopt it so the resolving resubmit targets that session
            // (NOT another lazy-create). Render the transient card.
            const env = resp as ClarificationEnvelope;
            const sid = env.session_id || null;
            setPendingClarification(env);
            maybePlayClarificationSound(env, sid, crid);
            scrollThreadToLatest();
        return sid;
      }
      if (
            isTopicClarificationEnvelope(
              resp as Turn | ClarificationEnvelope | TopicClarificationEnvelope
            )
      ) {
            // Broad readers on the lazy-create path. The backend minted a
            // real session and returned its id on the envelope; adopt it so
            // the focus resubmit targets that session.
            const env = resp as TopicClarificationEnvelope;
            const sid = env.session_id || null;
            setPendingTopicClarification(env);
            maybePlayClarificationSound(env, sid, crid);
            scrollThreadToLatest();
        return sid;
      }
      const { session_id, turn } = resp as {
            session_id: string;
            turn: Turn;
      };
      setPendingClarification(null);
      setPendingTopicClarification(null);
      setTurns((prev) => [...prev, turn]);
      if (cardResponse?.chat_card.kind === 'training_action') {
        replaceChatCard(cardResponse.chat_card);
      }
      scrollThreadToLatest('answer-start');
          // The user bubble renders from `turn.user_msg`; the in-flight
          // optimistic bubble is cleared by the caller's finally block.
          // Telemetry: a successful v2 lazy-create response without a
          // truthy `session_id` would silently route follow-ups back to
          // /sessions/new/v2/turn. Surface for diagnosis.
      if (!session_id) {
            // eslint-disable-next-line no-console
            console.warn(
              '[homechat] v2 lazy-create response missing session_id',
              { session_id, turn }
            );
      }
      return session_id || null;
    },
    [isStaleResolve, applyConstructionSowChatView, applyGpcChatView, applyMrChatView, replaceChatCard, hydrateDirectSearchThread, maybePlayClarificationSound, deliverResponseLlmResultEvents, scrollThreadToLatest, pageContext]
  );

  const runPrimingTurn = useCallback(
    async (
      target: string,
      text: string,
      finalQuestion?: string,
      regime?: FarRegime,
      focus?: {
        focusToken?: string;
        topicFocus?: string;
        priorClarificationQuestions?: string[];
      },
    ) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      interactionRevisionRef.current += 1;
      // Hold the question in `inFlightUserMsg` so the optimistic user
      // bubble renders BELOW any prior turns.
      setInFlightUserMsg(trimmed);
      // Hold the clarified text (final ≠ original) so the "Clarified: …"
      // callout renders in the in-flight area while the answer generates.
      // "Use my original" (final == original) → no callout.
      setInFlightClarification(
        finalQuestion && finalQuestion !== trimmed ? finalQuestion : null
      );
      // Hold the chosen focus (non-empty → not Skip) so the "Focused on: …"
      // callout renders in the in-flight area while the focused synth runs.
      setInFlightTopicFocus(focus?.topicFocus ? focus.topicFocus : null);
      const userMsg: ChatMessage = { role: 'user', content: trimmed };
      const crid = makeRequestId();
      setPendingRequestIdSynced(crid);
      setInFlight(true);
      // Homepage priming can invoke this helper directly with ``new`` before
      // the user has clicked + New Chat. Adopt the same client-only identity
      // that an ordinary lazy send uses so the immediate sessions refresh can
      // associate the server's activity/client_request_id with this request.
      // Without it, the response is still pending and Stop is disabled until
      // the long POST finally returns a session id.
      if (target === 'new') {
        activeSessionIdRef.current = PENDING_ID;
        setActiveSessionId(PENDING_ID);
      }
      scrollThreadToLatest();
      // Surface the parallel-generated title in the rail before the answer
      // (see `scheduleInFlightTitleRefresh`). One bounded one-shot refresh.
      scheduleInFlightTitleRefresh(crid);
      try {
        if (target === 'new') {
          // Lazy-create path mints a session and runs the multi-part
          // pipeline in one v2 call.
          // The server allocates a session id; we adopt it for any
          // subsequent turns.
          const lazyTurn = sendLazyCreateV2Turn(
            trimmed,
            crid,
            finalQuestion,
            regime,
            focus
          );
          refreshAfterTurnDispatch();
          const newSid = await lazyTurn;
          // Stale-resolve guard: the user switched/created/deleted a
          // session while the lazy-create was in flight → do NOT adopt
          // the new id or run the blank-popup recovery (both would
          // clobber the pane the user navigated to).
          if (isStaleResolve(crid)) {
            // no-op
          } else if (newSid) {
            // Update the merge ref synchronously (not via the deferred
            // sync effect) so the `void refreshSessions()` below keeps THIS
            // just-minted, still-turn-less session visible in the rail.
            activeSessionIdRef.current = newSid;
            setActiveSessionId(newSid);
          } else {
            // Degraded path: the lazy-create response did not surface a
            // `session_id` (backend quirk / serialization race). The
            // session DOES exist server-side because the pipeline ran;
            // the user just sees a blank popup because the empty-state
            // placeholder (`activeSessionId === null && messages.length === 0`)
            // hides the rendered turn. Recover by refetching the sessions
            // list and adopting the most recent one — it is almost
            // certainly the session we just created (sub-second latency
            // between create and refetch). Without this fallback, the
            // homepage-submit popup goes BLANK and follow-ups silently
            // create yet another fresh session.
            // eslint-disable-next-line no-console
            console.warn(
              '[homechat] runPrimingTurn(new) returned no session_id; attempting recovery via sessions refetch'
            );
            try {
              const resp = await listSessions();
              const recent = (resp.sessions || []).find(
                (s) => s.session_id !== PENDING_ID
              );
              if (recent?.session_id) {
                setActiveSessionId(recent.session_id);
                setSessions(resp.sessions || []);
              }
            } catch {
              // Silent — the outer notify('error') from the catch block
              // below would already mask this. If listSessions itself
              // fails, the panel stays in its degraded state but the
              // user can recover by clicking + New chat.
            }
          }
        } else {
          const turn = sendV2Turn(
            target,
            trimmed,
            crid,
            finalQuestion,
            regime,
            focus
          );
          refreshAfterTurnDispatch();
          await turn;
          // Belt-and-suspenders: the priming caller has already dispatched
          // setActiveSessionId(target) before invoking runPrimingTurn, but
          // re-dispatching here makes it impossible for any future caller
          // to forget. Same-value setState is a no-op in React. Skip when
          // the dispatch went stale (user navigated away mid-flight) so we
          // don't yank the active session back to the abandoned target.
          if (!isStaleResolve(crid)) {
            setActiveSessionId(target);
          }
        }
        void refreshSessions();
      } catch (e: any) {
        // Switching conversations clears ownership of this request. The old
        // session's optimistic message and failure must not be appended to the
        // newly selected transcript.
        if (isStaleResolve(crid)) return;
        if (isExpectedCancellationError(e)) {
          await reconcileExpectedCancellation(crid);
          return;
        }
        const msg: string = e?.message || '';
        // The optimistic bubble disappears when in-flight state is cleared.
        // Preserve the submitted request as a normal transcript message when
        // the server rejects the response, matching the ordinary send path.
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          return last?.role === 'user' && last.content === userMsg.content
            ? prev
            : [...prev, userMsg];
        });
        if (isTurnInProgressError(e)) {
          notify(
            'warning',
            'Another response is in progress. Try again in a few seconds.'
          );
          // This rollback is harmless for rehydrated legacy messages and
          // keeps the input reusable after an in-progress rejection.
          setMessages((prev) =>
            prev.length > 0 && prev[prev.length - 1] === userMsg
              ? prev.slice(0, -1)
              : prev
          );
        } else if (e instanceof FastAnswerRecoveryError) {
          // Compatibility for a stale server response: current context
          // fallback is server-internal, so never offer a client-side mode
          // switch. Surface a normal safe error instead of a blank turn.
          notify('error', e.message);
        } else if (isUnavailableWorkflowError(e)) {
          notify('warning', INTENT_CHOICE_UNAVAILABLE_COPY);
        } else if (/503|llm_unavailable|reference_data_unavailable/i.test(msg)) {
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: REFUSAL_COPY,
              refusal: true,
              refusal_reason: /llm_unavailable/i.test(msg)
                ? 'llm_unavailable'
                : 'reference_data_unavailable',
            } as ChatMessage,
          ]);
        } else if (e instanceof HomeChatHttpError && e.status === 500) {
          // Masked reader crash: backend now returns 500 with a friendly
          // `detail.error`. Render it inline as a refusal-styled bubble
          // (never the error_code / technical detail).
          setMessages((prev) => [
            ...prev,
            {
              role: 'assistant',
              content: friendlyTurnErrorMessage(e),
              refusal: true,
              refusal_reason: 'turn_failed',
            } as ChatMessage,
          ]);
        } else {
          notify('error', 'Failed to send message.');
        }
        scrollThreadToLatest();
      } finally {
        // §4.1 state hygiene — IN-FLIGHT teardown via the authoritative
        // shared reset. Clears the four in-flight fields but DELIBERATELY
        // leaves `pendingClarification` untouched (on the vague path it was
        // just set to render the ClarificationCard and must survive this
        // finally). `pendingRequestId` is cleared via `clearPendingIfOwned`
        // so a late finally can't clobber a newer turn's live id. The
        // in-flight `final_question` was a local arg to this call and is
        // dropped here; `pendingClarification` is reset on the answer/resolve
        // paths and in `handleProgressComplete` so a stale `final_question`
        // can never skip the gate next turn.
        if (!isStaleResolve(crid)) {
          resetInFlightTurnState({
            setInFlight,
            clearPendingRequestId: () => clearPendingIfOwned(crid),
            setInFlightUserMsg,
            setInFlightClarification,
            setInFlightTopicFocus,
          });
        }
      }
    },
    [notify, refreshSessions, refreshAfterTurnDispatch, sendLazyCreateV2Turn, sendV2Turn, isStaleResolve, reconcileExpectedCancellation, clearPendingIfOwned, setPendingRequestIdSynced, scheduleInFlightTitleRefresh, scrollThreadToLatest]
  );

  return {
    acceptCompanionResponse,
    sendV2Turn,
    sendLazyCreateV2Turn,
    runPrimingTurn,
  };
}

export type HomeChatTurnSubmission = ReturnType<typeof useHomeChatTurnSubmission>;
